import { useState, useEffect } from 'react'
import axios from 'axios'
import './App.css'

// Import components
import ImageUploader from './components/ImageUploader'
import StyleSelector from './components/StyleSelector'
import TransformedImage from './components/TransformedImage'
import Gallery from './components/Gallery'
import LoadingOverlay from './components/LoadingOverlay'
import ErrorMessage from './components/ErrorMessage'

// Define backend URL - use relative path in production to avoid CORS and ad blockers
const BACKEND_URL = import.meta.env.PROD ? '/api' : 'http://localhost:3001'

function App() {
  // Core state
  const [uploadedImage, setUploadedImage] = useState(null)
  const [selectedStyle, setSelectedStyle] = useState(null)
  const [transformedImage, setTransformedImage] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Gallery state (would normally fetch from backend/localStorage)
  const [galleryImages, setGalleryImages] = useState(() => {
    const savedImages = localStorage.getItem('cartoonPfpGallery');
    return savedImages ? JSON.parse(savedImages) : [];
  })

  // Save gallery to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('cartoonPfpGallery', JSON.stringify(galleryImages));
  }, [galleryImages]);

  // Style options
  const styles = [
    { 
      name: 'Classic American Fun', 
      preview: '/placeholders/classic.png',
      description: 'Vibrant, smooth cartoon with exaggerated features' 
    },
    { 
      name: 'Modern Technical', 
      preview: '/placeholders/modern.png',
      description: '3D-rendered cartoon with realistic textures' 
    },
    { 
      name: 'Whimsical Anime', 
      preview: '/placeholders/anime.png',
      description: 'Soft, pastel anime style with expressive eyes' 
    },
    { 
      name: 'Superfrens Attack', 
      preview: '/placeholders/comic.png',
      description: 'Bold comic-book style with strong outlines' 
    },
  ]

  // Handle image upload and validation
  const handleImageUpload = (file, errorMessage) => {
    if (file) {
      setUploadedImage(file)
      setError(null)
      setTransformedImage(null)
    } else if (errorMessage) {
      setError(errorMessage)
      setUploadedImage(null)
    }
  }

  // Handle style selection
  const handleStyleSelect = (styleName) => {
    console.log('Selected style:', styleName)
    setSelectedStyle(styleName)
  }

  // Initiate transformation
  const handleTransform = async () => {
    if (!uploadedImage || !selectedStyle) {
      setError('Please upload an image and select a style first.')
      return
    }
    
    console.log(`Starting transformation with style: ${selectedStyle}`)
    setIsLoading(true)
    setError(null)
    setTransformedImage(null)

    const formData = new FormData()
    formData.append('image', uploadedImage)
    formData.append('style', selectedStyle)

    try {
      const response = await axios.post(`${BACKEND_URL}/transform`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: false,
        timeout: 60000, // 60 seconds
      })

      console.log('Backend Response:', response.data)

      if (response.data && response.data.imageUrl) {
        setTransformedImage(response.data.imageUrl)
        
        // Add to gallery
        const newImage = {
          id: Date.now().toString(),
          url: response.data.imageUrl,
          style: selectedStyle,
          date: new Date().toISOString(),
        }
        
        setGalleryImages(prev => [newImage, ...prev])
      } else {
        throw new Error('Invalid response received from server.')
      }
    } catch (err) {
      console.error('Transformation API Error:', err)
      let errorMessage = 'Transformation failed. Please try again later.'
      
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage = err.response.data.error
      } else if (err.message) {
        errorMessage = err.message
      } else if (err.code === 'ERR_NETWORK') {
        errorMessage = 'Network error - the server may be down or blocked by your browser.'
      }
      
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // Clear result and try again
  const handleTryAnother = () => {
    setTransformedImage(null)
    setSelectedStyle(null)
    // Optionally keep or clear uploadedImage:
    // setUploadedImage(null)
  }

  // Select an image from the gallery
  const handleSelectFromGallery = (image) => {
    // In a real app, we might want to download this image or use it as a reference
    setTransformedImage(image.url)
    setSelectedStyle(image.style || null)
  }

  return (
    <div className="bg-gray-50 flex flex-col font-sans text-gray-800">
      {/* Loading Overlay */}
      <LoadingOverlay 
        isLoading={isLoading} 
        message={`Transforming to ${selectedStyle} style...`} 
      />
      
      {/* Header */}
      <header className="bg-white shadow-sm py-2 sticky top-0 z-10 flex-shrink-0">
        <div className="container mx-auto px-3 flex justify-between items-center">
          <h1 className="text-lg font-bold text-primary">Cartoon PFP Maker</h1>
          <p className="text-xs text-gray-500">Turn photos into cartoon avatars</p>
        </div>
      </header>

      {/* Main Content Area - Reverted to simpler structure, no flex-grow, centered items */}
      <main className="container mx-auto px-3 py-3 flex flex-col items-center w-full">
        {/* Error Message Area */}
        <ErrorMessage 
          error={error} 
          onDismiss={() => setError(null)} 
        />

        {/* Conditional Rendering: Show Upload/Style OR Preview/Download */}
        {!transformedImage ? (
          <div className="flex flex-col items-center w-full max-w-xl">
            {/* Image Uploader */}
            <ImageUploader 
              onImageUpload={handleImageUpload} 
              isLoading={isLoading} 
            />
              
            {/* Style Selector */}
            <StyleSelector
              styles={styles}
              selectedStyle={selectedStyle}
              onStyleSelect={handleStyleSelect}
              isLoading={isLoading}
            />

            {/* Gallery - Limited height with scroll */}
            {galleryImages.length > 0 && (
              <div className="w-full mt-2 mb-3">
                <Gallery 
                  images={galleryImages} 
                  onImageSelect={handleSelectFromGallery} 
                />
              </div>
            )}

            {/* Transform Button - conditionally shown */}
            {uploadedImage && selectedStyle && (
              <button
                onClick={handleTransform}
                disabled={isLoading}
                className="w-full max-w-md bg-primary text-white font-bold py-2.5 px-6 rounded-lg shadow-sm hover:bg-red-500 transition duration-200 disabled:opacity-50 disabled:cursor-wait mt-1"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Transforming...
                  </span>
                ) : (
                  `Transform to ${selectedStyle}!`
                )}
              </button>
            )}
          </div>
        ) : (
          /* Transformed Image Display - Centered */
          <div className="flex justify-center w-full">
            <TransformedImage
              imageUrl={transformedImage}
              selectedStyle={selectedStyle}
              onTryAnother={handleTryAnother}
            />
          </div>
        )}
      </main>
    </div>
  )
}

export default App
